import Cookies from 'js-cookie';
import { apiService, API_ENDPOINTS } from '@/shared/services';
import { LoginCredentials, RegisterData, User } from '../types';
import { APIResponse } from '@/shared/types';

export class AuthService {
  private static readonly USER_KEY = 'user';
  private static readonly TOKEN_KEY = 'token';
 
  static async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      const response = await apiService.post<APIResponse<{ user: User; token?: string; tokens?: { accessToken?: string } }>>(
        API_ENDPOINTS.auth.login,
        credentials
      );

      const data = response.data.data!;
      const user = data.user;
      
      // Support token in either data.token or data.tokens.accessToken
      const token = (data as any).token || (data as any).tokens?.accessToken || '';

      if (token) {
        Cookies.set(this.TOKEN_KEY, token, { expires: 7, secure: false, sameSite: 'lax' });
      }
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return { user, token };
    } catch (error: any) {
      console.error('Login API error:', error);
      
      // Extract error message from different possible error structures
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error?.response?.status === 403) {
        errorMessage = 'Account deactivated. Please contact your HR manager.';
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  static async register(userData: RegisterData): Promise<{ user: User; token: string }> {
    try {
      const response = await apiService.post<APIResponse<{ user: User; token?: string; tokens?: { accessToken?: string } }>>(
        API_ENDPOINTS.auth.register,
        userData
      );

      const data = response.data.data!;
      const user = data.user;
      const token = (data as any).token || (data as any).tokens?.accessToken || '';
      if (token) {
        Cookies.set(this.TOKEN_KEY, token, { expires: 7, secure: false, sameSite: 'lax' });
      }
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return { user, token };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  }

  static async logout(): Promise<void> {
    
    try {
      await apiService.post(API_ENDPOINTS.auth.logout);
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    }
    
    
    // 1. Clear all cookies
    Cookies.remove(this.TOKEN_KEY);
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach(cookieName => {
      Cookies.remove(cookieName);
    });
    
    // 2. Clear localStorage
    const localStorageKeys = Object.keys(localStorage);
    localStorage.clear();
    
    // 3. Clear sessionStorage
    sessionStorage.clear();
    
    // Verify localStorage is actually empty
    if (localStorage.length === 0) {
    } else {
      console.warn('  ⚠️ localStorage still has items:', Object.keys(localStorage));
    }
    
    // 4. Clear IndexedDB (async but don't wait)
    if (window.indexedDB) {
      window.indexedDB.databases?.().then(databases => {
        databases.forEach(db => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
      }).catch(error => {
        console.warn('Could not clear IndexedDB:', error);
      });
    }
    
    // 5. Clear Cache Storage (async but don't wait)
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        return Promise.all(
          cacheNames.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
      }).catch(error => {
        console.warn('Could not clear cache storage:', error);
      });
    }
    
  }

  static async validateToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      // Use the session validation endpoint
      const response = await apiService.get(API_ENDPOINTS.auth.validate);
      return response.data.success;
    } catch (error: any) {
      console.error('Token validation failed:', error);
      
      // Check if it's a session conflict error
      if (error?.response?.data?.error?.includes('logged in from another device')) {
        // Clear local auth data and return false to trigger logout
        await this.logout();
        return false;
      }
      
      return false;
    }
  }

  static getToken(): string | null {
    return Cookies.get(this.TOKEN_KEY) || null;
  }

  static getUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  static isAuthenticated(): boolean {
    const user = this.getUser();
    const token = this.getToken();
    return !!user && !!token;
  }

  static getUserRole(): string | null {
    const user = this.getUser();
    return user?.role || null;
  }

  /**
   * Request password reset
   */
  static async forgotPassword(email: string): Promise<APIResponse<null>> {
    try {
      const response = await apiService.post<APIResponse<null>>(
        API_ENDPOINTS.auth.forgotPassword,
        { email }
      );
      return response.data;
    } catch (error: any) {
      console.error('Forgot password API error:', error);
      throw new Error(error.response?.data?.message || 'Failed to send password reset email');
    }
  }

  /**
   * Reset password with token
   */
  static async resetPassword(token: string, password: string, confirmPassword: string): Promise<APIResponse<null>> {
    try {
      const response = await apiService.post<APIResponse<null>>(
        API_ENDPOINTS.auth.resetPassword,
        { token, password, confirmPassword }
      );
      return response.data;
    } catch (error: any) {
      console.error('Reset password API error:', error);
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  }

  /**
   * Verify reset token
   */
  static async verifyResetToken(token: string): Promise<APIResponse<{ email: string }>> {
    try {
      const response = await apiService.get<APIResponse<{ email: string }>>(
        `${API_ENDPOINTS.auth.verifyResetToken}/${token}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Verify reset token API error:', error);
      throw new Error(error.response?.data?.message || 'Invalid or expired reset token');
    }
  }
}
